import { useState } from "react";
import { api } from "../../api/client";
import type { StaffSnapshot } from "../../api/types";
import { useAppState } from "../../state/appState";
import { useToasts } from "../../state/toasts";
import { Empty, Modal, Toggle } from "../../ui/components";
import { dateTime, money } from "../../ui/format";

type PromoCode = StaffSnapshot["promoCodes"][number];

const SCOPE_LABELS: Record<string, string> = {
  order: "Весь заказ",
  products: "Выбранные товары",
  categories: "Выбранные категории"
};

const WEEKDAYS = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];

export function PromoCodesPage() {
  const { snapshot } = useAppState();
  const { run } = useToasts();
  const [editing, setEditing] = useState<PromoCode | null | undefined>(undefined);

  if (!snapshot) return <Empty>Загрузка данных…</Empty>;

  const codes = snapshot.promoCodes;

  async function toggleActive(promo: PromoCode, active: boolean) {
    await run(() => api.patch(`/api/admin/promo-codes/${promo.id}`, { active }));
  }

  async function remove(promo: PromoCode) {
    if (!confirm(`Удалить промокод ${promo.code}? История его применений тоже удалится.`)) return;
    await run(() => api.delete(`/api/admin/promo-codes/${promo.id}`), "Промокод удалён");
  }

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Маркетинг</p>
          <h1>Промокоды</h1>
        </div>
        <button type="button" className="btn btn-sm" onClick={() => setEditing(null)}>
          + Промокод
        </button>
      </div>

      <section className="panel">
        <header>
          <div>
            <h2>Действующие коды</h2>
            <p>К одному заказу применяется не более одного промокода.</p>
          </div>
        </header>

        {codes.length === 0 ? (
          <Empty>Промокодов пока нет.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Код</th>
                  <th>Скидка</th>
                  <th>Условия</th>
                  <th>Расписание</th>
                  <th>Применения</th>
                  <th>Активен</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {codes.map(promo => (
                  <tr key={promo.id}>
                    <td className="num">
                      <strong>{promo.code}</strong>
                      <br />
                      <small className="muted">{SCOPE_LABELS[promo.scope]}</small>
                    </td>
                    <td className="num">
                      {promo.type === "percent" ? `${promo.value}%` : money(promo.value)}
                      {promo.maxDiscount ? (
                        <>
                          <br />
                          <small className="muted">не более {money(promo.maxDiscount)}</small>
                        </>
                      ) : null}
                    </td>
                    <td className="tiny">
                      {promo.minAmount ? `от ${money(promo.minAmount)}` : "без минимума"}
                      <br />
                      <span className="muted">
                        {promo.stacksWithPromotions ? "совместим с акциями" : "не совместим с акциями"}
                      </span>
                    </td>
                    <td className="tiny">
                      {promo.startsAt || promo.endsAt
                        ? `${dateTime(promo.startsAt)} — ${dateTime(promo.endsAt)}`
                        : "без срока"}
                      <br />
                      <span className="muted">
                        {promo.weekdays.length ? promo.weekdays.map(day => WEEKDAYS[day]).join(", ") : "любой день"}
                        {promo.hourFrom !== null && promo.hourTo !== null
                          ? `, ${promo.hourFrom}:00–${promo.hourTo}:00`
                          : ""}
                      </span>
                    </td>
                    <td className="num">
                      {promo.usedCount}
                      {promo.usageLimit ? ` / ${promo.usageLimit}` : ""}
                      {promo.usageLimitPerPhone ? (
                        <>
                          <br />
                          <small className="muted">{promo.usageLimitPerPhone} на телефон</small>
                        </>
                      ) : null}
                    </td>
                    <td>
                      <Toggle
                        checked={promo.active}
                        label={`Промокод ${promo.code} активен`}
                        onChange={next => void toggleActive(promo, next)}
                      />
                    </td>
                    <td>
                      <div className="row-actions">
                        <button type="button" className="btn btn-sm" onClick={() => setEditing(promo)}>
                          Изменить
                        </button>
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => void remove(promo)}>
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing !== undefined ? (
        <PromoForm
          promo={editing}
          snapshot={snapshot}
          onClose={() => setEditing(undefined)}
        />
      ) : null}
    </>
  );
}

function PromoForm({
  promo,
  snapshot,
  onClose
}: {
  promo: PromoCode | null;
  snapshot: StaffSnapshot;
  onClose: () => void;
}) {
  const { run } = useToasts();
  const [form, setForm] = useState({
    code: promo?.code ?? "",
    type: promo?.type ?? "percent",
    value: String(promo?.value ?? 10),
    minAmount: String(promo?.minAmount ?? 0),
    maxDiscount: promo?.maxDiscount ? String(promo.maxDiscount) : "",
    scope: promo?.scope ?? "order",
    productSlugs: promo?.productSlugs ?? [],
    categorySlugs: promo?.categorySlugs ?? [],
    weekdays: promo?.weekdays ?? [],
    hourFrom: promo?.hourFrom !== null && promo?.hourFrom !== undefined ? String(promo.hourFrom) : "",
    hourTo: promo?.hourTo !== null && promo?.hourTo !== undefined ? String(promo.hourTo) : "",
    usageLimit: promo?.usageLimit ? String(promo.usageLimit) : "",
    usageLimitPerPhone: promo?.usageLimitPerPhone ? String(promo.usageLimitPerPhone) : "",
    stacksWithPromotions: promo?.stacksWithPromotions ?? true
  });

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm(current => ({ ...current, [key]: value }));
  }

  const optionalNumber = (value: string) => (value.trim() ? Number(value) : null);

  async function save() {
    const body = {
      code: form.code.trim(),
      type: form.type,
      value: Number(form.value) || 0,
      minAmount: Number(form.minAmount) || 0,
      maxDiscount: optionalNumber(form.maxDiscount),
      scope: form.scope,
      productSlugs: form.scope === "products" ? form.productSlugs : [],
      categorySlugs: form.scope === "categories" ? form.categorySlugs : [],
      weekdays: form.weekdays,
      hourFrom: optionalNumber(form.hourFrom),
      hourTo: optionalNumber(form.hourTo),
      usageLimit: optionalNumber(form.usageLimit),
      usageLimitPerPhone: optionalNumber(form.usageLimitPerPhone),
      stacksWithPromotions: form.stacksWithPromotions
    };

    const ok = await run(
      () => (promo ? api.patch(`/api/admin/promo-codes/${promo.id}`, body) : api.post("/api/admin/promo-codes", body)),
      promo ? "Промокод сохранён" : "Промокод создан"
    );
    if (ok) onClose();
  }

  return (
    <Modal
      title={promo ? `Промокод ${promo.code}` : "Новый промокод"}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!form.code.trim()}>
            Сохранить
          </button>
        </>
      }
    >
      <div className="field-row">
        <label className="field">
          <span>Код</span>
          <input
            autoFocus
            value={form.code}
            onChange={event => update("code", event.target.value.toUpperCase())}
            placeholder="PARK10"
          />
        </label>
        <label className="field">
          <span>Тип скидки</span>
          <select value={form.type} onChange={event => update("type", event.target.value as typeof form.type)}>
            <option value="percent">Процент</option>
            <option value="fixed">Фиксированная сумма</option>
          </select>
        </label>
        <label className="field">
          <span>{form.type === "percent" ? "Процент" : "Сумма, ₽"}</span>
          <input inputMode="numeric" value={form.value} onChange={event => update("value", event.target.value)} />
        </label>
      </div>

      <div className="field-row">
        <label className="field">
          <span>Минимальная сумма, ₽</span>
          <input
            inputMode="numeric"
            value={form.minAmount}
            onChange={event => update("minAmount", event.target.value)}
          />
        </label>
        <label className="field">
          <span>Максимальная скидка, ₽</span>
          <input
            inputMode="numeric"
            value={form.maxDiscount}
            onChange={event => update("maxDiscount", event.target.value)}
            placeholder="без ограничения"
          />
        </label>
      </div>

      <label className="field">
        <span>Применяется к</span>
        <select value={form.scope} onChange={event => update("scope", event.target.value as typeof form.scope)}>
          <option value="order">Всему заказу</option>
          <option value="products">Выбранным товарам</option>
          <option value="categories">Выбранным категориям</option>
        </select>
      </label>

      {form.scope === "products" ? (
        <SlugPicker
          label="Товары"
          options={snapshot.products.map(product => ({ id: product.id, title: product.title }))}
          selected={form.productSlugs}
          onChange={next => update("productSlugs", next)}
        />
      ) : null}

      {form.scope === "categories" ? (
        <SlugPicker
          label="Категории"
          options={snapshot.categories.map(category => ({ id: category.id, title: category.title }))}
          selected={form.categorySlugs}
          onChange={next => update("categorySlugs", next)}
        />
      ) : null}

      <ScheduleFields
        weekdays={form.weekdays}
        hourFrom={form.hourFrom}
        hourTo={form.hourTo}
        onWeekdays={next => update("weekdays", next)}
        onHourFrom={next => update("hourFrom", next)}
        onHourTo={next => update("hourTo", next)}
      />

      <div className="field-row">
        <label className="field">
          <span>Всего применений</span>
          <input
            inputMode="numeric"
            value={form.usageLimit}
            onChange={event => update("usageLimit", event.target.value)}
            placeholder="без ограничения"
          />
        </label>
        <label className="field">
          <span>Применений на телефон</span>
          <input
            inputMode="numeric"
            value={form.usageLimitPerPhone}
            onChange={event => update("usageLimitPerPhone", event.target.value)}
            placeholder="без ограничения"
          />
        </label>
      </div>

      <label className="inline-check">
        <input
          type="checkbox"
          checked={form.stacksWithPromotions}
          onChange={event => update("stacksWithPromotions", event.target.checked)}
        />
        Можно применять вместе с акциями
      </label>
    </Modal>
  );
}

export function SlugPicker({
  label,
  options,
  selected,
  onChange
}: {
  label: string;
  options: { id: string; title: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="field">
      <span>{label}</span>
      <div className="opt-list">
        {options.map(option => (
          <label className={`opt ${selected.includes(option.id) ? "picked" : ""}`} key={option.id}>
            <input
              type="checkbox"
              checked={selected.includes(option.id)}
              onChange={event =>
                onChange(
                  event.target.checked
                    ? [...selected, option.id]
                    : selected.filter(id => id !== option.id)
                )
              }
            />
            {option.title}
          </label>
        ))}
      </div>
    </div>
  );
}

export function ScheduleFields({
  weekdays,
  hourFrom,
  hourTo,
  onWeekdays,
  onHourFrom,
  onHourTo
}: {
  weekdays: number[];
  hourFrom: string;
  hourTo: string;
  onWeekdays: (next: number[]) => void;
  onHourFrom: (next: string) => void;
  onHourTo: (next: string) => void;
}) {
  return (
    <>
      <div className="field">
        <span>Дни недели</span>
        <div className="opt-list">
          {WEEKDAYS.map((title, day) => (
            <label className={`opt ${weekdays.includes(day) ? "picked" : ""}`} key={day}>
              <input
                type="checkbox"
                checked={weekdays.includes(day)}
                onChange={event =>
                  onWeekdays(
                    event.target.checked ? [...weekdays, day] : weekdays.filter(item => item !== day)
                  )
                }
              />
              {title}
            </label>
          ))}
        </div>
        <small className="muted tiny">Ничего не выбрано — действует в любой день.</small>
      </div>

      <div className="field-row">
        <label className="field">
          <span>Час начала</span>
          <input inputMode="numeric" value={hourFrom} onChange={event => onHourFrom(event.target.value)} placeholder="—" />
        </label>
        <label className="field">
          <span>Час окончания</span>
          <input inputMode="numeric" value={hourTo} onChange={event => onHourTo(event.target.value)} placeholder="—" />
        </label>
      </div>
    </>
  );
}
